import React, { useEffect, useMemo, useState } from 'react';
import '../../css/components/VehicleManagement/VehicleSheet/VehicleSheet.css';

import { useUser } from '../../hooks/useUser';
import { delay } from '../../utils';
import { DataTable } from '../DataTable';
import { Message, ImageModal } from '../index';
import RoleGuard from '../RoleGuard/RoleGuard';
import { VehicleDataColumns } from '../../constants';
import { roleConfig } from '../../constants/roleConfig';
import { GenericModal } from '../GenericModal/GenericModal';
import { EditVehicleModal } from './EditVehicleModal/EditVehicleModal';
import { CreateVehicleModal } from './CreateVehicleModal/CreateVehicleModal';
import { UploadVehicleImageModal } from './UploadVehicleImageModal/UploadVehicleImageModal';

import { getCompanies } from '../../service/api/Company/apiCompany';
import { createVehicle, deleteVehicle } from '../../service/api/Vehicle/apiVehicle';
import { getDeliver } from '../../service/api/Deliver/apiDeliver';

const VehicleSheet = ({ data, onRefresh }) => {
    const { companyId, id, role } = useUser();
    const REACT_APP_API_URL = process.env.REACT_APP_API_URL || window.APP_CONFIG.REACT_APP_API_URL;
    const [vehicleData, setVehicleData] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editImage, setEditImage] = useState(null);
    const [imageModal, setImageModal] = useState({ open: false, src: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [mode, setMode] = useState(companyId ? 'company' : 'personal');
    const [companies, setCompanies] = useState([]);
    const [drivers, setDrivers] = useState([]);

    const closeImage = () => setImageModal({ open: false, src: "" });

    // 根據模式過濾車輛資料
    const filteredByMode = vehicleData.filter(item => {
        if (mode === 'personal') {
            return !item.company; // 個人模式：company 為空
        } else {
            return item.company; // 公司模式：company 有值
        }
    });

    const filteredItems = useMemo(() => {
        return filteredByMode.filter(item => {
            return item.plate?.toLowerCase().includes(filterText.toLowerCase()) ||
                item.model?.toLowerCase().includes(filterText.toLowerCase()) ||
                String(item.employeeId).includes(filterText);
        });
    }, [filteredByMode, filterText]);

    useEffect(() => {
        if (data) {
            setVehicleData(data);
        }
    }, [data]);

    useEffect(() => {
        if (!isEditOpen && !isCreateOpen) return;
        // 打開編輯 modal 時查詢公司
        let queryParams = null;
        if (role === roleConfig['主管']) {
            queryParams = { id: companyId };
        } else if (role === roleConfig['員工']) {
            queryParams = { id: companyId };
        }
        getCompanies(queryParams)
            .then(response => {
                if (response?.status === 200 && response.data?.result) {
                    setCompanies(response.data.result);
                }
            })
            .catch(e => console.error('查詢公司失敗:', e));

        const fetchDrivers = async () => {
            try {
                let queryParams = {};
                if (role === roleConfig['主管']) {
                    queryParams = companyId ? { companyId } : { id };
                } else if (role === roleConfig['員工']) {
                    queryParams = { id };
                }
                const response = await getDeliver(queryParams);
                if (response.status === 200 && response.data.status === 1) {
                    const driversList = (response.data.result || []).filter(driver =>
                        (mode === 'personal' && !driver.company) ||
                        (mode === 'company' && driver.company)
                    );
                    setDrivers(driversList);
                }
            } catch (error) {
                console.error('獲取司機列表失敗:', error);
            }
        };
        fetchDrivers();
    }, [isEditOpen, isCreateOpen]);

    const handleAddNew = () => {
        setSelectedVehicle(null);
        setIsCreateOpen(true);
    };

    const handleEdit = (vehicle) => {
        setSelectedVehicle(vehicle);
        setIsEditOpen(true);
    };

    const handleImageUpload = (vehicle) => {
        setEditImage(vehicle);
    };

    const handleDelete = async (vehicleId) => {
        if (window.confirm("確定要刪除此車輛嗎？")) {
            try {
                setIsLoading(true);
                const response = await deleteVehicle(vehicleId);
                if (response?.status === 200) {
                    Message(response.data.message, 'success');
                    await delay(1);
                    onRefresh();
                }
            } catch (error) {
                console.error('刪除車輛失敗:', error);
                Message('刪除車輛失敗', 'error');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            setIsLoading(true);
            // 公司模式下，company 欄位應為空或 null
            const submitData = {
                ...formData,
                company: (mode === 'personal' || !formData.company) ? null : formData.company
            };
            const response = await createVehicle(submitData);

            if (response?.status === 200) {
                Message(response.data.message, 'success');
                await delay(1);
                setIsCreateOpen(false);
                onRefresh();
            }
        } catch (error) {
            console.error('新增失敗:', error);
            Message('新增失敗', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setIsCreateOpen(false);
        setIsEditOpen(false);
        setSelectedVehicle(null);
    };

    const allColumns = [
        {
            name: "車輛圖片",
            selector: row => {
                const imagePaths = Array.isArray(row.imagePath) ? row.imagePath : (row.imagePath ? [row.imagePath] : []);

                if (!imagePaths || imagePaths.length === 0) {
                    return <div className="furniture-thumbs"><div className="thumb placeholder">無圖片</div></div>;
                }

                const maxShow = 2;
                const visibleImages = imagePaths.slice(0, maxShow);
                const hiddenCount = imagePaths.length - maxShow;

                return (
                    <div className="furniture-thumbs">
                        {visibleImages.map((imgPath, i) => (
                            <div
                                key={i}
                                className="thumb-wrapper"
                                onClick={() =>
                                    setImageModal({
                                        open: true,
                                        src: null,
                                        gallery: imagePaths.map(
                                            p => `${REACT_APP_API_URL}/public${p}`
                                        ),
                                    })
                                }
                            >
                                <img
                                    src={`${REACT_APP_API_URL}/public${imgPath}`}
                                    alt={`車輛 ${i + 1}`}
                                    className="thumb"
                                />
                                {i === visibleImages.length - 1 && hiddenCount > 0 && (
                                    <div className="thumb-overlay">+{hiddenCount}</div>
                                )}
                            </div>
                        ))}
                    </div>
                );
            }, key: "image"
        },
        ...VehicleDataColumns,
        {
            name: "操作",
            cell: row =>
                <>
                    {!row.deletedAt && (
                        <div className="row-actions">
                            <button className="btn btn-action--primary btn-action--sm" onClick={() => handleEdit(row)}>
                                編輯
                            </button>
                            <button className="btn btn-action--confirm btn-action--sm" onClick={() => handleImageUpload(row)}>
                                上傳圖片
                            </button>
                            <RoleGuard requiredRole={roleConfig['主管']}>
                                <button className="btn btn-action--danger btn-action--sm" onClick={() => handleDelete(row.id)}>
                                    刪除
                                </button>
                            </RoleGuard>
                        </div>
                    )}
                </>,
            key: "action"
        },
    ];

    // 根據模式過濾列
    const filteredAllColumns = allColumns.filter(col => !col.mode || col.mode === mode);
    const [selectedColumns, setSelectedColumns] = useState(
        filteredAllColumns
            .map(col => col.key)
        // .filter(key => key !== "action")
    );
    const toggleColumn = key => setSelectedColumns(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    const columnsToShow = filteredAllColumns.filter(col => selectedColumns.includes(col.key));

    return (
        <>
            {imageModal.open && (
                <ImageModal
                    image={imageModal}
                    closeImage={closeImage} />
            )}

            <GenericModal isOpen={isCreateOpen}>
                <CreateVehicleModal
                    onSubmit={handleFormSubmit}
                    onCancel={() => { setIsCreateOpen(false); }}
                    mode={mode}
                    companies={companies}
                    drivers={drivers}
                />
            </GenericModal>
            <GenericModal isOpen={isEditOpen}>
                <EditVehicleModal
                    vehicle={selectedVehicle}
                    onClose={() => { setIsEditOpen(false); }}
                    onRefresh={onRefresh}
                    mode={mode}
                    companies={companies}
                    drivers={drivers}
                />
            </GenericModal>

            {/* 圖片上傳 */}
            <GenericModal
                isOpen={!!editImage}
                onClose={() => { setEditImage(null) }}
            >
                <UploadVehicleImageModal
                    vehicle={editImage}
                    onClose={() => { setEditImage(null); }}
                    onRefresh={onRefresh}
                />
            </GenericModal>

            <div className="card-header d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-2 gap-md-0">
                <div className="d-flex align-items-center">
                    <h6 className="m-0 fw-bold text-primary">
                        <i className="fas fa-car me-2"></i>車輛列表
                    </h6>
                </div>
                <div className="sheet-actions">
                    <button
                        onClick={() => setMode(mode === 'personal' ? 'company' : 'personal')}
                        disabled={role < roleConfig['財務']}
                        className={`btn btn-action--sm ${mode === 'personal' ? 'btn-action--primary btn-action--soft' : 'btn-action--confirm btn-action--soft'
                            }`}
                        title={role < roleConfig['財務'] ? '該帳號無法切換模式' : ''}
                    >
                        {mode === 'personal' ? '👤 當前個人模式' : '🏢 當前公司模式'}
                    </button>
                    <input
                        type="text"
                        placeholder="搜尋"
                        className="form-control ms-2 me-2 mt-3 mb-3"
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        style={{ maxWidth: "200px" }}
                    />
                    <button
                        onClick={() => handleAddNew()}
                        className="btn btn-action--confirm"
                    >
                        <i className="fas fa-plus me-2"></i>新增車輛
                    </button>
                </div>
            </div>

            <div className="card-body border-top sheet-toolbar">
                <div className="sheet_filter_container">
                    <div className="sheet_filter">
                        <div className="sheet-actions">
                            <button
                                className="btn btn-action--neutral btn-action--soft btn-action--sm"
                                onClick={() => setShowColumnSelector(prev => !prev)}
                            >
                                {showColumnSelector ? "收起欄位" : "選擇欄位"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                    {showColumnSelector &&
                        filteredAllColumns.map(col => {
                            const isSelected = selectedColumns.includes(col.key);
                            return (
                                <button
                                    key={col.key}
                                    type="button"
                                    className={`btn btn-action--sm ${isSelected ? 'btn-action--primary' : 'btn-action--neutral btn-action--soft'}`}
                                    onClick={() => toggleColumn(col.key)}
                                >
                                    {col.name} {isSelected && <span className="badge bg-light text-dark ms-1">✓</span>}
                                </button>
                            )
                        })
                    }
                </div>
            </div>

            <DataTable
                columns={columnsToShow}
                data={filteredItems}
                pagination
                noDataComponent="暫無車輛資料"
                paginationComponentOptions={{
                    rowsPerPageText: '每頁筆數',
                    rangeSeparatorText: '總共',
                }}
            />
        </>
    );
};

export { VehicleSheet };
